'use client'

import type {
	DropzoneOptions,
	DropzoneState,
	FileRejection,
	FileWithPath,
} from 'react-dropzone'

import * as React from 'react'
import { composeEventHandlers } from '@radix-ui/primitive'
import { Primitive } from '@radix-ui/react-primitive'
import { useDropzone } from 'react-dropzone'

export type DropzoneContextProps = DropzoneState & DropzoneOptions

const DropzoneContext = React.createContext<DropzoneContextProps>(
	{} as DropzoneContextProps
)

export const useDropzoneContext = () => React.use(DropzoneContext)

export type DropzoneProps = {
	children: React.ReactNode | ((state: DropzoneContextProps) => React.ReactNode)
} & DropzoneOptions

export const Dropzone = ({ children, ...props }: DropzoneProps) => {
	const state = useDropzone(props)

	const context = { ...state, ...props }

	return (
		<DropzoneContext value={context}>
			{typeof children === 'function' ? children(context) : children}
		</DropzoneContext>
	)
}
Dropzone.displayName = 'Dropzone'

export const DropzoneInput = (
	props: React.ComponentProps<typeof Primitive.input>
) => {
	const { getInputProps, disabled } = useDropzoneContext()

	return (
		<Primitive.input
			ref={props.ref}
			{...getInputProps({ disabled, ...props })}
		/>
	)
}

export const DropzoneZone = (
	props: React.ComponentProps<typeof Primitive.div>
) => {
	const {
		getRootProps,
		isFocused,
		isDragActive,
		isDragAccept,
		isDragReject,
		isFileDialogActive,
		preventDropOnDocument,
		noClick,
		noKeyboard,
		noDrag,
		noDragEventsBubbling,
		disabled,
	} = useDropzoneContext()

	return (
		<Primitive.div
			ref={props.ref}
			data-prevent-drop-on-document={preventDropOnDocument ? true : undefined}
			data-no-click={noClick ? true : undefined}
			data-no-keyboard={noKeyboard ? true : undefined}
			data-no-drag={noDrag ? true : undefined}
			data-no-drag-events-bubbling={noDragEventsBubbling ? true : undefined}
			data-disabled={disabled ? true : undefined}
			data-focused={isFocused ? true : undefined}
			data-drag-active={isDragActive ? true : undefined}
			data-drag-accept={isDragAccept ? true : undefined}
			data-drag-reject={isDragReject ? true : undefined}
			data-file-dialog-active={isFileDialogActive ? true : undefined}
			{...getRootProps(props)}
		/>
	)
}

export const DropzoneTrigger = ({
	onClick,
	...props
}: React.ComponentProps<typeof Primitive.button>) => {
	const { open } = useDropzoneContext()

	return (
		<Primitive.button
			onClick={composeEventHandlers(onClick, open)}
			{...props}
		/>
	)
}

export type DropzoneDragAcceptedProps = {
	children?: React.ReactNode
}

export const DropzoneDragAccepted = ({
	children,
}: DropzoneDragAcceptedProps) => {
	const { isDragAccept } = useDropzoneContext()

	if (!isDragAccept) {
		return null
	}

	return children
}

export type DropzoneDragRejectedProps = {
	children?: React.ReactNode
}

export const DropzoneDragRejected = ({
	children,
}: DropzoneDragRejectedProps) => {
	const { isDragReject } = useDropzoneContext()

	if (!isDragReject) {
		return null
	}

	return children
}

export type DropzoneDragDefaultProps = {
	children?: React.ReactNode
}

export const DropzoneDragDefault = ({ children }: DropzoneDragDefaultProps) => {
	const { isDragActive } = useDropzoneContext()

	if (isDragActive) {
		return null
	}

	return children
}

export type DropzoneAcceptedProps = {
	children: (acceptedFiles: readonly FileWithPath[]) => React.ReactNode
}

export const DropzoneAccepted = ({ children }: DropzoneAcceptedProps) => {
	const { acceptedFiles } = useDropzoneContext()

	return children(acceptedFiles)
}

export type DropzoneRejectedProps = {
	children: (fileRejections: readonly FileRejection[]) => React.ReactNode
}

export const DropzoneRejected = ({ children }: DropzoneRejectedProps) => {
	const { fileRejections } = useDropzoneContext()

	return children(fileRejections)
}

const Root = Dropzone
const Input = DropzoneInput
const Zone = DropzoneZone
const Trigger = DropzoneTrigger
const DragAccepted = DropzoneDragAccepted
const DragRejected = DropzoneDragRejected
const DragDefault = DropzoneDragDefault
const Accepted = DropzoneAccepted
const Rejected = DropzoneRejected

export {
	Root,
	Input,
	Zone,
	Trigger,
	DragAccepted,
	DragRejected,
	DragDefault,
	Accepted,
	Rejected,
}
